import hyperdom from 'hyperdom'
import {isEqual, get} from 'lodash-es'
import router from './router.js'
import * as roomsKey from '../../../packages/accommodation/roomsKey.js'
import * as extraStayKey from './extraStayKey.js'
import * as extrasUrlKeys from './extrasUrlKeys.js'
import * as frequentTravellersDiscountKey from '../server/frequentTravellersDiscountKey.js'
import Modals from './modals.jsx'
import createApi from './api.js'
import http from './http.js'
import Flights from './flights/index.jsx'
import PromisedFlights from './promisedFlights/index.jsx'
import parsePreselectedFlightSearchString from './promisedFlights/utils/parsePreselectedFlightSearchString.js'
import QuoteEmail from './quoteEmail.js'
import QuoteCriteria from './quoteCriteria.js'
import Promotions from './promotions.jsx'
import Faq from './faq.jsx'
import Header from './header.jsx'
import urlSanitising from './urlSanitising.js'
import * as fby from './fby.js'
import * as extole from './extole.js'
import GenesysChat from './genesysChat/genesysChat.jsx'
import * as gtmDataLayer from './analytics/gtm/dataLayer.js'
import {partySize, regionalDomains} from 'root-config/constants.js'
import * as sticky from './sticky.js'
import mergeHotelAvailabilities from '../../../packages/accommodation/hotels/mergeHotelAvailabilities.js'
import findIndex from 'lowscore/findIndex.js'
import {config as tabs, getTabName} from './tabs.js'
import emailFormSchema from './emailFormSchema.js'
import * as validation from './validation.js'
import deepExtend from 'deep-extend'
import addTravelAgentDiscountsToDeparture from '../../../packages/price-engine/addTravelAgentDiscountsToDeparture.js'
import assetPath from './assetPath.js'
import availableStatus from '../../../packages/departure/availableStatus.js'
import ServerErrorsPanel from './serverErrorsPanel.jsx'
import dmpgDataLayer from './dmpg/dmpgDataLayer.js'
import * as gtmScriptLoader from './analytics/gtm/scriptsLoader.js'
import uuid from 'uuid'
import SSO from './sso.js'
import TourHeader from './tourHeader.jsx'
import discountsArray from '../../../packages/price-engine/discountsArray.js'
import browserCapabilities from './browserCapabilities.js'
import {getItineraryImage, getTourHeaderDetails} from './tourDetailsUtils.js'
import {getTripDetails} from './tripSummaryUtils.js'
import FeefoRating from './feefoRating.jsx'
import TripDetails from './tripDetails.jsx'
import bookWithConfidenceBanner from './bookWithConfidenceBanner.jsx'
import responsiveRender from './responsiveRender.js'
import TravelProtectionPlanType from 'root-packages/travelProtection/index.js'
import {isDayTour} from 'root-packages/tourTypes.js'

import {
  Booking,
  TourOverviewTab,
  ExtrasTab,
  PassengersBooking,
  PassengersDetails,
  PassengersTab,
  PaymentTab,
  ConfirmationTab,
  Summary,
  Sidebar,
  Navigation,
  Footer,
} from './defaultFlow/index.js'

import {
  BookingDayTour,
  TourOverviewDayTour,
  SidebarDayTour,
  SummaryDayTour,
  PassengersTabDayTour,
  PassengersBookingDayTour,
  PassengersDetailsDayTour,
  PaymentTabDayTour,
  ConfirmationTabDayTour,
  TourHeaderDayTour,
} from './dayTourFlow/index.js'

import {createDataDog} from './datadog/index.js'
import format from 'root-core/utils/text/stringFormatFilter.js'
import KnoCommerce from './knoCommerce.js'
import AnotherTabDetector from './anotherTabDetector.js'

const routes = {
  tour: router.route('/:sellingCompanyCode/:tourCode/:departureCode'),
}

class App {
  constructor (options) {
    this.user = {}
    this.progress = {}
    this.browserCapabilities = options.config.browserCapabilities || browserCapabilities

    fby.init()
    const brandBSContainerIds = options.config.brandBookingServicesContainerIds
    gtmScriptLoader.init({
      bookingServicesContainerIds: options.config.bookingServicesContainerIds,
      brandGtmContainerIds: options.config.gtmContainerIds,
      brandBookingServicesContainerIds:
        brandBSContainerIds &&
        (brandBSContainerIds[options.config.environment] ||
        brandBSContainerIds.default),
      optimizelyId: options.config.optimizelyId,
      whitelabelGtmContainerIds: options.config.whitelabelGtmContainerIds,
    })
    this.supportedCardTypes =[]
    this.dmpg = dmpgDataLayer(window.bookingServicesDataLayer, options.dmpgCartId || uuid.v4())

    this.hideLoading()
    this.departure = {}
    this.modals = new Modals()
    this.year = new Date().getFullYear()
    this.tourUrlParams = {}
    this.config = options.config
    this.features = this.config.features
    this.availablePaymentMethods = options.availablePaymentMethods

    this.api = options.api || createApi({
      webUrl: options.config.webUrl,
      http: options.http || http,
    })
    this.api.onError = response => this.serverErrorHandler(response)
    this.api.onSuccess = () => this.serverErrorsPanel.clear()

    this.serverErrorsPanel = options.serverErrorsPanel || new ServerErrorsPanel()

    this.isChrome = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime)
    this.isIE = /*@cc_on!@*/false || !!document.documentMode
    // this.isSafari = /constructor/i.test(window.HTMLElement) || (function (p) { return p.toString() === "[object SafariRemoteNotification]"})(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification))
    this.isFirefox = typeof InstallTrigger !== 'undefined'
    this.currentBrowser = ''

    this.progress.currentTab = tabs[0]
    this.allPromotions = []
    const authType = new URLSearchParams(window.location.search).get('authType') || ''
    this.authStrategy = authType.toLowerCase() === 'auth' ? 'auth' : 'sso'
    this.sso = new SSO(this.config, this.authStrategy)

    this.header = new Header({
      config: this.config,
      user: this.user,
      showFaq: this.showFaq.bind(this),
      priceReady: this.priceReady.bind(this),
      total: this.total.bind(this),
      api: this.api,
      sso: this.sso,
      browserCapabilities: this.browserCapabilities,
    })

    if (options.pageNotFound) {
      this.departure = {status: 'missing'}
      return
    }

    sticky.bind(() => {
      return this.summary && !this.summary.showBreakdown && this.summary.visibleAvailablePromotionCount() <= 3
    })

    this.emailFormSchema = emailFormSchema(this.config)
    this.hasArrived = false
    this.disableLoadingTimeout = options.disableLoadingTimeout
    this.experienceVersion = 'BookingServicesBeta'

    if (this.features.extole && this.features.extole.enabled) {
      extole.init(this.features.extole.url)
    }

    this.datadog = createDataDog({
      config: this.config,
      user: this.user,
      datadogLogs: options.datadogLogs,
    })

    this.brandLogo = this.config.brandLogo

    this.anotherTab = new AnotherTabDetector(this.config.brandName)
  }

  refresh () {}

  isFirstArrival () {
    if (!this.hasArrived) {
      this.hasArrived = true
      return true
    }
    return false
  }

  getDefaultTab () {
    return 'tour-overview'
  }

  checkCurrentBrowser () {
    if(this.isChrome){return this.currentBrowser = 'Chrome'}
    if(this.isIE){return this.currentBrowser = 'Internet Explorer'}
    if(this.isSafari){return this.currentBrowser = 'Safari'}
    if(this.isFirefox){return this.currentBrowser = 'Mozilla Firefox'}
    return undefined
  }


  async loadCriteria (params) {
    var selectedRooms = params.rooms ? roomsKey.parse(params.rooms) : undefined
    const pickupLocation = params.pickupLocation
    var adults = parseInt(params.adults) || 0
    var children = parseInt(params.children) || 0
    var preselectedFlight = parsePreselectedFlightSearchString(params.preselectedFlight)
    const protect = extrasUrlKeys.parse(params.protect, TravelProtectionPlanType.standard)
    const protectPlus = extrasUrlKeys.parse(params.protectPlus, TravelProtectionPlanType.plus)
    const travelProtection = Array(Math.max(protect.length, protectPlus.length))
      .fill(undefined).map((_, i) => protect[i] || protectPlus[i])

    if (adults < 0 || adults > partySize.max) adults = 0
    if (children < 0 || children > partySize.max) children = 0
    if (adults + children > partySize.max) children = 0

    this.quoteCriteria.setUrlParams({
      pickupLocation,
      rooms: selectedRooms,
      adults,
      children,
      childAges: params.childages ? params.childages.split('-').map(age => parseInt(age, 10)) : [],
      travelProtection,
      ontarioResident: extrasUrlKeys.parse(params.ontario),
      prepaidGratuities: extrasUrlKeys.parse(params.gratuities),
      frequentTravellerDiscount: frequentTravellersDiscountKey.parse(params.frequent),
      epd: params.epd,
      lmdRemoved: params.lmdRemoved,
      hotelBefore: extraStayKey.parse(params.hotelBefore),
      hotelAfter: extraStayKey.parse(params.hotelAfter),
      ExternalRef: params.ExternalRef,
      childrenDoBs: params.childrenDoBs ? params.childrenDoBs.split('-') : [],
    })
    this.tourOverviewTab.partyPicker.party = this.quoteCriteria.party
    this.tourOverviewTab.partyPicker.updatePassengerOptions()

    await this.promisedFlights.preselectFlight(preselectedFlight)

    var quoteCriteriaUrlParamBindings = this.quoteCriteriaUrlParamBindings(true)

    var discountCodesDifferent = params.discountCode !== hyperdom.binding(quoteCriteriaUrlParamBindings.discountCode).get()
    var flightsDifferent = params.flights !== hyperdom.binding(quoteCriteriaUrlParamBindings.flights).get()

    this.refresh()
    if (discountCodesDifferent || flightsDifferent) {
      this.showLoading()
      try {
        await Promise.all([
          discountCodesDifferent ? this.loadPromotions(params.discountCode) : undefined,
          flightsDifferent ? this.quoteCriteria.setFlightTrip(params.flights, params.departureAirportTransfer, params.arrivalAirportTransfer) : undefined,
        ])
      } catch(ex) {
        console.error(ex)
      }
      this.progress.criteriaLoaded = true
      this.refresh()
    } else {
      this.progress.criteriaLoaded = true
    }
  }

  async loadPromotions (promotionCodes) {
    if (!promotionCodes) {
      return this.loadingPromotions = undefined
    }
    this.loadingPromotions = promotionCodes
    await Promise.all(promotionCodes
      .toString()
      .split(',')
      .map(code => this.summary.discountCode.loadPromotion(code))
    )
    this.loadingPromotions = undefined
  }

  loadGenesysChat () {
    if (this.features.genesysChat && this.features.genesysChat.enabled) {
      this.genesysChat = new GenesysChat({
        dmpg: this.dmpg,
        config: this.config,
        user: this.user,
        coBrowseEnabled: this.features.genesysChat.coBrowseEnabled,
        bookingSummary: this.summary,
      })
      this.summary.genesysChat = this.genesysChat
    }
  }


  addInlineOffers () {
    if (this.currentTabId === 'passengers') {
      const inlineOffers = this.allPromotions.filter(p => p.inlineOffer)
      setTimeout(() => {
        inlineOffers.forEach(p => this.promotions.addPromotion(p))
        this.refresh()
      }, this.config.inlineOfferDelaySeconds * 1000)
    }
  }

  async loadCountries () {
    this._countries = await this.api.countries()
    this.refresh()
  }

  async loadStatus (sellingCompanyCode, tourCode, departureCode) {
    return this.api.status(sellingCompanyCode, tourCode, departureCode)
  }

  async loadHotelAvailabilities (sellingCompanyCode, tourCode, departureCode) {
    const hotelAvailabilities = await this.api.hotelAvailabilities(sellingCompanyCode, tourCode, departureCode)
    mergeHotelAvailabilities(hotelAvailabilities, this.departure)
    this.quoteCriteria.hotelsNowHaveAvailabilities()
  }

  async ssoLogin () {
    const ssoLoginWithAuthStrategy = async token => {
      return 'auth' === this.authStrategy
        ? this.api.authLogin(token)
        : this.api.ssoLogin(token)
    }

    try {
      const tokens = [
        localStorage.getItem('TAToken'), // logged in by access-token endpoint - hack for localStorage partitioning
        await this.sso.getToken(),
      ]

      if (!tokens.some(Boolean)) {
        const result = await this.api.logout({ssoOnly: true})
        if (result.loggedOut) {
          this.user.role = 'consumer'
          this.user.loaded = true
        }

        return
      }

      let state = {}
      for (const token of tokens) {
        if (!token) continue
        state = await ssoLoginWithAuthStrategy(token)

        if (state.valid) break
      }

      if (!state.ignore) {
        this.isAuthUrl = true
      }
      if (!state.valid){
        await this.sso.logout()
      }
    } catch (err) {
      console.error(err)
    }
  }

  async validateUser (sellingCompanyCode) {
    const errorMessage = {
      title: 'Something went wrong, you have been logged out!',
      message: <p>You will need to sign in again at <a class='travel-agent-brand-home' href={this.config.brandPages.travelAgentHomePage}>{this.config.brandPages.travelAgentHomePage}</a></p>,
    }

    try {
      const user = await this.api.user(sellingCompanyCode)

      if (this.user.role === user.role) return true

      throw new Error()
    } catch(e) {
      document.querySelector('body').style.overflow = "hidden"
      this.user.validateError = errorMessage
      return false
    }
  }

  async loadUser (sellingCompanyCode, tourCode, departureCode) {
    try {
      const user = await this.api.user(sellingCompanyCode)
      deepExtend(this.user, user)
      this.user.isTravelAgent = this.user.role === 'travelAgent'
      this.user.loaded = true

      const experiments = get(this.config, 'features.experiments')
      const userExperiments = get(user.config, 'features.experiments')

      if (!isEqual(experiments, userExperiments) && this.user.isTravelAgent && !this.config.features.disableExperimentCheck) {
        return this.reloadApp()
      }

      const {clientAddressMandatory} = this.config.features
      deepExtend(this.config, user.config)

      if (this.departure.status === 'available' && (this.user.isTravelAgent || this.user.whitelabel)) {
        await this.setupDeparture(sellingCompanyCode, tourCode, departureCode)
      }

      if (user.whitelabel && typeof clientAddressMandatory === 'boolean') {
        this.config.features.clientAddressMandatory = clientAddressMandatory
      }
      if (this.user.isTravelAgent && this.userIsAllowedToBookForThisSellingCompany()) {
        await this.loadTravelAgencyData(sellingCompanyCode, tourCode, departureCode)
      }
      if (this.user.isTravelAgent && !this.userIsAllowedToBookForThisSellingCompany()) {
        this.datadog.warn("Travel agent doesn't have permission")
      }
      if (this.userIsLoggedOutOnAgentUrl()) {
        this.datadog.warn("User has been logged out")
      }
    } catch (ex) {
      this.user.status = (ex.statusCode === 404) ? 'missing' : 'internalError'
      throw ex
    }
  }

  loadSellingCompany (tourCode, departureCode) {
    this.tourCode = tourCode
    this.departureCode = departureCode
    this.extrasTestimonial = this.getRandomTestimonial()
    this.loadingTestimonial = this.getRandomTestimonial()
  }

  getRandomTestimonial () {
    return this.config.testimonials[Math.floor(Math.random() * this.config.testimonials.length)]
  }

  isDepartureLoaded (sellingCompanyCode, tourCode, departureCode) {
    return this.departure && this.departure.sellingCompanyCode == sellingCompanyCode && this.departure.productCode == tourCode && this.departure.code == departureCode
  }

  async setupDeparture (sellingCompanyCode, tourCode, departureCode) {
    try {
      const [, status] = await Promise.all([
        this.loadDeparture(sellingCompanyCode, tourCode, departureCode),
        this.loadStatus(sellingCompanyCode, tourCode, departureCode),
      ])
      this.departure.status = status.status || 'missing'
      this.departure.roomsOccupancy = status.roomsOccupancy || this.departure.roomsOccupancy
      this.progress.tourLoaded = true
    } catch (e) {
      this.departure.status = this.departure.status || 'missing'
    }
  }

  async load (sellingCompanyCode, tourCode, departureCode, paramTab) {
    this.showLoading(true)
    this.loadSellingCompany(tourCode, departureCode)
    try {
      const content = await this.api.content(sellingCompanyCode, tourCode, departureCode)
      if (content.errors){
        console.error('unable to load content', content.errors)
        this.content = {}
      } else {
        this.content = content
      }
    } catch(err) {
      this.content = {}
    }

    await this.setupDeparture(sellingCompanyCode, tourCode, departureCode)

    if (['missing', 'internalError'].includes(this.departure.status)) return

    await this.ssoLogin()
    await this.loadCountries()
    await this.loadUser(sellingCompanyCode, tourCode, departureCode)

    if (this.content && this.content.name) {
      this.departure.name = this.content.name
    }

    await this.loadAllPromotions(sellingCompanyCode, tourCode, departureCode)
    await this.loadCriteria(this.tourUrlParams)

    this.loadGenesysChat()

    this.addInlineOffers()

    const lmd = this.allPromotions.find(p => p.promotionSubtype === 'LMD')
    if(lmd && !this.quoteCriteria.lmdRemoved) {
      this.summary.discountCode.addAvailablePromotion(lmd)
    }

    this.tourOverviewTab.validateParty(this.quoteCriteria.party)
    if (!this.currentTabId || this.currentTabId === this.getDefaultTab()) {
      const landingTab = (paramTab === 'payment' || paramTab === 'confirmation') ? this.getDefaultTab() : paramTab
      this.switchToTab(landingTab, false)
    }

    this.hideLoading()
    this.datadog.info('Page was loaded', {departureStatus: this.departure.status})
  }

  async loadAllPromotions (sellingCompanyCode, tourCode, departureCode) {
    const promotions = await this.api.allPromotions(sellingCompanyCode, tourCode, departureCode)
    promotions.forEach(p => {
      this.allPromotions.push(p)
    })
  }

  async loadDeparture (sellingCompanyCode, tourCode, departureCode) {
    try {
      const departure = await this.api.departure(sellingCompanyCode, tourCode, departureCode)
      this.setDeparture(departure)
      await this.loadHotelAvailabilities(sellingCompanyCode, tourCode, departureCode)
    } catch (ex) {
      this.departure = {status: (ex.statusCode === 404) ? 'missing' : 'internalError'}
    }
    if (this.promisedFlights.isDepartureEligibleForPromisedFlights()) {
      await this.promisedFlights.loadData()
    }
  }

  async loadTravelAgencyData (sellingCompanyCode, tourCode, departureCode) {
    const [commision, discounts] = await Promise.all([
      this.api.travelAgentCommission(sellingCompanyCode, tourCode, departureCode),
      this.api.travelAgentDiscounts(sellingCompanyCode, tourCode, departureCode),
    ])
    this.user.travelAgentCommission = commision
    this.quoteCriteria.priceCache.clear()
    addTravelAgentDiscountsToDeparture(this.departure, discounts)
  }

  setDeparture (departure) {
    this.departure = departure
    this.setPageTitle(this.progress.currentTab)
    this.quoteCriteria = new QuoteCriteria({
      config: this.config,
      departure: this.departure,
      api: this.api,
    })

    this.promotions = new Promotions({
      quoteCriteria: this.quoteCriteria,
      currency: this.departure.prices.currency,
      modals: this.modals,
    })

    this.quoteEmail = new QuoteEmail({
      config: this.config,
      departure: this.departure,
      quoteCriteria: this.quoteCriteria,
      api: this.api,
    })

    this.promisedFlights = new PromisedFlights({
      config: this.config,
      departure: this.departure,
      api: this.api,
      quoteCriteria: this.quoteCriteria,
      showLoading: this.showLoading.bind(this),
      hideLoading: this.hideLoading.bind(this),
      disableLoadingTimeout: this.disableLoadingTimeout,
      modals: this.modals,
    })

    this.flights = new Flights({
      departure: this.departure,
      modals: this.modals,
      quoteCriteria: this.quoteCriteria,
      showLoading: this.showLoading.bind(this),
      hideLoading: this.hideLoading.bind(this),
      disableLoadingTimeout: this.disableLoadingTimeout,
      config: this.config,
      promotions: this.promotions,
      dmpg: this.dmpg,
      api: this.api,
      user: this.user,

      loadAirPromotions: () => {
        return this.allPromotions.filter(p => p.isAirPromotion)
      },
    })

    this.extrasTab = new ExtrasTab({
      config: this.config,
      departure: this.departure,
      quoteCriteria: this.quoteCriteria,
      modals: this.modals,
      promisedFlights: this.promisedFlights,
      flights: this.flights,
      next: this.showPassengers.bind(this),
    })

    if (this.config.features.realFeefoEnabled && this.config.feefoMerchantidentifier) {
      this.feefoRating = new FeefoRating({
        config: this.config,
        departure: this.departure,
      })
    }

    if (this.config.knoCommerce) {
      this.knoCommerce = new KnoCommerce({
        config: this.config,
        quoteCriteria: this.quoteCriteria,
      })
    }

    if (isDayTour(this.departure)) {
      this.setDayTourFlow()
    } else {
      this.setDefaultFlow()
    }

    window.addEventListener('resize', () => this.refresh())
  }

  setTourHeader () {
    this.tripDetails = new TripDetails({
      departure: this.departure,
      modals: this.modals,
      summary: this.summary,
      content: this.content,
      config: this.config,
    })

    this.tourHeader = new TourHeader({
      config: this.config,
      modals: this.modals,
      tourCode: this.tourCode,
      tripDetails: this.tripDetails,
      feefoRating: this.feefoRating,
      getDetails: () => getTourHeaderDetails(this.content, this.departure, this.quoteCriteria.passengers ? this.quoteCriteria.passengers.length : 0),
    })
  }

  setDefaultFlow () {
    this.faq = new Faq(this.config, this.config.faqs, this.user)
    this.brandLogo = this.config.brandLogo

    this.booking = new Booking({
      datadog: this.datadog,
      departure: this.departure,
      config: this.config,
      api: this.api,
      quoteCriteria: this.quoteCriteria,
      showLoading: this.showLoading.bind(this),
      hideLoading: this.hideLoading.bind(this),
      validateUser: (sellingCompanyCode) => this.validateUser(sellingCompanyCode),
      user: this.user,
    })

    this.navigation = new Navigation({
      availableTabs: () => this.availableTabs(),
      bookingComplete: () => this.bookingComplete(),
      isNavigable: (currentTabIndex, tabIndex) => this.isNavigable(currentTabIndex, tabIndex),
      getTabName: (tabId) => this.getTabName(tabId),
      navigateTab: ({tabId, currentTabIndex, tabIndex}) => this.navigateTab({tabId, currentTabIndex, tabIndex}),
    })

    this.summary = new Summary({
      user: this.user,
      api: this.api,
      config: this.config,
      priceBreakdown: this.priceBreakdown.bind(this),
      departure: this.departure,
      promotions: this.promotions,
      progress: this.progress,
      total: this.total.bind(this),
      discounts: this.discounts.bind(this),
      quoteCriteria: this.quoteCriteria,
      modals: this.modals,
      anyDiscounts: this.anyDiscounts.bind(this),
      totalBeforeDiscount: this.totalBeforeDiscount.bind(this),
      bookingCreated: () => this.progress.currentTab.bookingCreated,
      allPromotions: this.allPromotions,
      showLoading: this.showLoading.bind(this),
      hideLoading: this.hideLoading.bind(this),
      nextTab: () => this.nextTab(),
      showExtras: this.showExtras.bind(this),
      showPassengers: this.showPassengers.bind(this),
      bookNow: () => this.passengersTab.submit(),
      showEmailQuote: this.showEmailQuote.bind(this),
      clearErrors: () => this.serverErrorsPanel.clear(),
      showRoomOnRequestModal: () => this.tourOverviewTab.showRoomOnRequestModal(),
      showTripOnRequestModal: () => this.tourOverviewTab.showTripOnRequestModal(),
      isExtrasAvailable: () => this.extrasIsAvailable(),
      getCurrentTabId: () => this.currentTabId,
    })

    this.passengersDetails = new PassengersDetails({
      api: this.api,
      browserCapabilities: this.browserCapabilities,
      config: this.config,
      countries: () => this.countries(),
      departure: this.departure,
      dmpg: this.dmpg,
      quoteCriteria: this.quoteCriteria,
      summary: this.summary,
      showLoading: this.showLoading.bind(this),
      hideLoading: this.hideLoading.bind(this),
    })

    this.passengersBooking = new PassengersBooking({
      api: this.api,
      booking: this.booking,
      browserCapabilities: this.browserCapabilities,
      config: this.config,
      departure: this.departure,
      features: this.features,
      modals: this.modals,
      passengersDetails: this.passengersDetails,
      extraStays: this.extrasTab.extraStays,
      quoteCriteria: this.quoteCriteria,
      quoteEmail: this.quoteEmail,
      user: this.user,
      countries: () => this.countries(),
      hideLoading: () => this.hideLoading(),
      showLoading: () => this.showLoading(),
      switchToTab: (...args) => this.switchToTab(...args),
    })

    this.passengersTab = new PassengersTab({
      user: this.user,
      config: this.config,
      departure: this.departure,
      quoteCriteria: this.quoteCriteria,
      api: this.api,
      modals: this.modals,
      features: this.features,
      serverErrorsPanel: this.serverErrorsPanel,
      priceBreakdown: this.priceBreakdown.bind(this),
      browserCapabilities: this.browserCapabilities,
      passengersBooking: this.passengersBooking,

      showLoading: () => this.showLoading(),
      promotions: this.promotions,
      bookingCreated: () => this.progress.currentTab.bookingCreated,
      progress: this.progress,
      hideLoading: () => this.hideLoading(),
      clearErrors: () => this.serverErrorsPanel.clear(),
    })

    this.setTourHeader()

    this.paymentTab = new PaymentTab({
      config: this.config,
      availablePaymentMethods: this.availablePaymentMethods,
      datadog: this.datadog,
      departure: this.departure,
      countries: this.countries.bind(this),
      paymentSuccessful: this.paymentSuccessful.bind(this),
      showLoading: this.showLoading.bind(this),
      hideLoading: this.hideLoading.bind(this),
      api: this.api,
      modals: this.modals,
      discounts: this.discounts.bind(this),
      anyDiscounts: this.anyDiscounts.bind(this),
      totalBeforeDiscount: this.totalBeforeDiscount.bind(this),
      total: this.total.bind(this),
      quoteCriteria: this.quoteCriteria,
      booking: this.booking,
      switchToTab: this.switchToTab.bind(this),
      user: this.user,
      serverErrorsPanel: this.serverErrorsPanel,
      priceBreakdown: this.priceBreakdown.bind(this),
      browserCapabilities: this.browserCapabilities,
      itineraryImage: (this.content) ? this.getItineraryImage() : undefined,
    })

    this.confirmationTab = new ConfirmationTab({
      departure: this.departure,
      config: this.config,
      countries: () => this.countries(),
      showLoading: (...args) => this.showLoading(...args),
      hideLoading: () => this.hideLoading(),
      api: this.api,
      booking: this.booking,
      user: this.user,
      serverErrorsPanel: this.serverErrorsPanel,
      browserCapabilities: this.browserCapabilities,
      tourHeader: this.tourHeader,
      modals: this.modals,
      getTripDetails: () => getTripDetails({
        booking: this.booking,
        discounts: this.discounts(),
        departure: this.departure,
        confirmation: this.booking.confirmation,
        airports: this.quoteCriteria.flights.airports || this.quoteCriteria.promisedFlights.airports,
      }),
      knoCommerce: this.knoCommerce,
    })

    this.sidebar = new Sidebar({
      config: this.config,
      summary: this.summary,
      user: this.user,
      displayFaq: () => this.displayFaq(),
      showEmailQuote: () => this.showEmailQuote(),
    })

    this.footer = new Footer({
      config: this.config,
      progress: this.progress,
      year: this.year,
      user: this.user,
      departure: this.departure,
      booking: this.booking,
      brandLogo: this.brandLogo,
    })

    this.tourOverviewTab = new TourOverviewTab({
      user: this.user,
      config: this.config,
      departure: this.departure,
      quoteCriteria: this.quoteCriteria,
      modals: this.modals,
      flights: this.flights,
      promisedFlights: this.promisedFlights,
      extraStays: this.extrasTab.extraStays,
      progress: this.progress,
      nextTab: () => this.nextTab(),
      passengersBooking: this.passengersBooking,
    })
  }

  setDayTourFlow () {
    this.faq = new Faq(this.config, this.config.dayTourFlow.faqs, this.user)
    this.brandLogo = this.config.dayTourFlow.brandLogo

    this.booking = new BookingDayTour({
      datadog: this.datadog,
      departure: this.departure,
      config: this.config,
      api: this.api,
      quoteCriteria: this.quoteCriteria,
      showLoading: this.showLoading.bind(this),
      hideLoading: this.hideLoading.bind(this),
      validateUser: (sellingCompanyCode) => this.validateUser(sellingCompanyCode),
      user: this.user,
    })

    this.passengersDetails = new PassengersDetailsDayTour({
      api: this.api,
      browserCapabilities: this.browserCapabilities,
      config: this.config,
      countries: () => this.countries(),
      departure: this.departure,
      dmpg: this.dmpg,
      quoteCriteria: this.quoteCriteria,
      summary: this.summary,
      showLoading: this.showLoading.bind(this),
      hideLoading: this.hideLoading.bind(this),
    })

    this.passengersBooking = new PassengersBookingDayTour({
      api: this.api,
      booking: this.booking,
      browserCapabilities: this.browserCapabilities,
      config: this.config,
      departure: this.departure,
      features: this.features,
      modals: this.modals,
      passengersDetails: this.passengersDetails,
      extraStays: this.extrasTab.extraStays,
      quoteCriteria: this.quoteCriteria,
      quoteEmail: this.quoteEmail,
      user: this.user,
      countries: () => this.countries(),
      hideLoading: () => this.hideLoading(),
      showLoading: () => this.showLoading(),
      switchToTab: (...args) => this.switchToTab(...args),
    })

    this.passengersTab = new PassengersTabDayTour({
      user: this.user,
      config: this.config,
      departure: this.departure,
      quoteCriteria: this.quoteCriteria,
      api: this.api,
      modals: this.modals,
      features: this.features,
      serverErrorsPanel: this.serverErrorsPanel,
      priceBreakdown: this.priceBreakdown.bind(this),
      browserCapabilities: this.browserCapabilities,
      passengersBooking: this.passengersBooking,

      showLoading: () => this.showLoading(),
      promotions: this.promotions,
      bookingCreated: () => this.progress.currentTab.bookingCreated,
      progress: this.progress,
      hideLoading: () => this.hideLoading(),
      clearErrors: () => this.serverErrorsPanel.clear(),
    })

    this.summary = new SummaryDayTour({
      user: this.user,
      api: this.api,
      config: this.config,
      priceBreakdown: this.priceBreakdown.bind(this),
      departure: this.departure,
      promotions: this.promotions,
      progress: this.progress,
      total: this.total.bind(this),
      discounts: this.discounts.bind(this),
      quoteCriteria: this.quoteCriteria,
      modals: this.modals,
      anyDiscounts: this.anyDiscounts.bind(this),
      totalBeforeDiscount: this.totalBeforeDiscount.bind(this),
      bookingCreated: () => this.progress.currentTab.bookingCreated,
      allPromotions: this.allPromotions,
      showLoading: this.showLoading.bind(this),
      hideLoading: this.hideLoading.bind(this),
      nextTab: () => this.nextTab(),
      showExtras: this.showExtras.bind(this),
      showPassengers: this.showPassengers.bind(this),
      bookNow: () => this.passengersTab.submit(),
      showEmailQuote: this.showEmailQuote.bind(this),
      clearErrors: () => this.serverErrorsPanel.clear(),
      showRoomOnRequestModal: () => this.tourOverviewTab.showRoomOnRequestModal(),
      showTripOnRequestModal: () => this.tourOverviewTab.showTripOnRequestModal(),
      isExtrasAvailable: () => this.extrasIsAvailable(),
      getCurrentTabId: () => this.currentTabId,
    })

    this.tripDetails = new TripDetails({
      departure: this.departure,
      modals: this.modals,
      summary: this.summary,
      content: this.content,
      config: this.config,
    })

    this.tourHeader = new TourHeaderDayTour ({
      config: this.config,
      modals: this.modals,
      tourCode: this.tourCode,
      tripDetails: this.tripDetails,
      feefoRating: this.feefoRating,
      quoteCriteria: this.quoteCriteria,
      getDetails: () => getTourHeaderDetails(this.content, this.departure, this.quoteCriteria.passengers ? this.quoteCriteria.passengers.length : 0),
    })

    this.paymentTab = new PaymentTabDayTour({
      config: this.config,
      availablePaymentMethods: this.availablePaymentMethods,
      datadog: this.datadog,
      departure: this.departure,
      countries: this.countries.bind(this),
      paymentSuccessful: this.paymentSuccessful.bind(this),
      showLoading: this.showLoading.bind(this),
      hideLoading: this.hideLoading.bind(this),
      api: this.api,
      modals: this.modals,
      discounts: this.discounts.bind(this),
      anyDiscounts: this.anyDiscounts.bind(this),
      totalBeforeDiscount: this.totalBeforeDiscount.bind(this),
      total: this.total.bind(this),
      quoteCriteria: this.quoteCriteria,
      booking: this.booking,
      switchToTab: this.switchToTab.bind(this),
      user: this.user,
      serverErrorsPanel: this.serverErrorsPanel,
      priceBreakdown: this.priceBreakdown.bind(this),
      browserCapabilities: this.browserCapabilities,
      itineraryImage: (this.content) ? this.getItineraryImage() : undefined,
    })

    this.navigation = new Navigation({
      availableTabs: () => this.availableTabs(),
      bookingComplete: () => this.bookingComplete(),
      isNavigable: (currentTabIndex, tabIndex) => this.isNavigable(currentTabIndex, tabIndex),
      getTabName: (tabId) => this.getTabName(tabId),
      navigateTab: ({tabId, currentTabIndex, tabIndex}) => this.navigateTab({tabId, currentTabIndex, tabIndex}),
    })

    this.sidebar = new SidebarDayTour({
      config: this.config,
      summary: this.summary,
      user: this.user,
      displayFaq: () => this.displayFaq(),
      showEmailQuote: () => this.showEmailQuote(),
    })

    this.footer = new Footer({
      config: this.config,
      progress: this.progress,
      year: this.year,
      user: this.user,
      departure: this.departure,
      booking: this.booking,
      brandLogo: this.brandLogo,
    })

    this.confirmationTab = new ConfirmationTabDayTour ({
      departure: this.departure,
      config: this.config,
      countries: () => this.countries(),
      showLoading: (...args) => this.showLoading(...args),
      hideLoading: () => this.hideLoading(),
      api: this.api,
      booking: this.booking,
      user: this.user,
      serverErrorsPanel: this.serverErrorsPanel,
      browserCapabilities: this.browserCapabilities,
      tourHeader: this.tourHeader,
      modals: this.modals,
      getTripDetails: () => getTripDetails({
        booking: this.booking,
        discounts: this.discounts(),
        departure: this.departure,
        confirmation: this.booking.confirmation,
        airports: this.quoteCriteria.flights.airports || this.quoteCriteria.promisedFlights.airports,
      }),
    })

    this.tourOverviewTab = new TourOverviewDayTour({
      user: this.user,
      config: this.config,
      departure: this.departure,
      quoteCriteria: this.quoteCriteria,
      modals: this.modals,
      flights: this.flights,
      promisedFlights: this.promisedFlights,
      extraStays: this.extrasTab.extraStays,
      progress: this.progress,
      nextTab: () => this.nextTab(),
      passengersBooking: this.passengersBooking,
    })
  }

  pushInitialDataToGtm () {
    gtmDataLayer.push({
      'enterpriseKey': '',
      'sellingCompanyCode': this.config.sellingCompanyCode,
      'brandCode': this.config.sellingCompanyCode.substring(0, 2),
      'salesGeoRegion': this.config.sellingCompanyCode.substring(2),
      'isAgentUi': this.user.isTravelAgent.toString(),
      'isTesting': this.config.environment !== 'production',
      'applicationId': 'BookingServices',
      'bookingType': this.user.isTravelAgent ? 'TA' : 'CD',
      'UserType': this.user.isTravelAgent ? 'CT' : 'CD',
      'salesRegion': this.config.sellingCompanyCode,
      'regionalDomainPrefix': regionalDomains[this.config.sellingCompanyCode.substring(2)],
    })

    gtmDataLayer.push({stageId: this.progress.currentTab.id, event: 'stage_changed', tourCode: this.departure.productCode})
    // Notify GA that traffic is coming from BS
    gtmDataLayer.push({experienceVersion: this.experienceVersion, event: 'experienceVersion'})
  }

  countries () {
    return this._countries || {}
  }

  clearLoadingTimeout () {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout)
    }
  }

  showLoading (firstLoad) {
    const show = () => {
      this.progress.tourLoading = true
      this.refresh()
    }
    if (firstLoad) {
      show()
    } else {
      if (!this.disableLoadingTimeout) {
        this.clearLoadingTimeout()
        this.loadingTimeout = setTimeout(show, 140)
      } else {
        show()
      }
    }
  }

  hideLoading () {
    this.clearLoadingTimeout()
    this.progress.tourLoading = false
    if (this.refresh) {
      this.refresh()
    }
  }

  onadd () {
    gtmDataLayer.activateOptimize()
  }

  onupdate () {
    gtmDataLayer.activateOptimize()
  }

  showFaq () {
    return this.config.faqs.length > 0
      && this.progress.currentTab != this.findTabById('confirmation')
      && this.departure.status !== 'missing'
  }

  priceReady () {
    return this.progress.criteriaLoaded
  }

  bookingCreated (booking) {
    this.booking = booking
    this.initPaymentObject()

    if (this.booking.TourOnRequest) {
      this.switchToTab('confirmation')
    } else {
      this.switchToTab('payment')
    }
  }

  paymentSuccessful () {
    this.switchToTab('confirmation')
    this.booking.complete = true
  }

  serverErrorHandler (response) {
    var errors = response.errors || response.ErrorMessages
    let errorMessage = `Error ocurred from API`
    let meta = {errors}
    if (errors && errors[0] && !errors[0].startsWith('Internal Server Error')) {
      this.serverErrorsPanel.setErrors(errors)
      console.error(errors)
    } else {
      this.serverErrorsPanel.setErrors(['Sorry, an unexpected error occurred.'])
      console.error(response)
      const errorFormatRe = /^Internal Server Error (?<errorId>.*) at (?<location>.*)/
      const errorMatch = errors && errors[0] && errors[0].match(errorFormatRe)
      if (errorMatch) {
        const {errorId, location} = errorMatch.groups
        errorMessage += ` at ${location}`
        meta.errorId = errorId
      }
    }
    this.datadog.error(errorMessage, meta)
    window.scrollTo(0, 0)
    this.hideLoading()
  }

  extrasIsAvailable () {
    const extrasEnabled = isDayTour(this.departure)
      ? this.config.dayTourFlow?.extrasEnabled
      : this.config.features.extrasEnabled
    if (!extrasEnabled) return false
    return this._extrasAvailable
      ? this._extrasAvailable
      : this._extrasAvailable = (() => {
        const {flights, features} = this.config
        const {prePostAccommodation} = features.extras
        if (this.quoteCriteria) {
          const {hotelsBefore, hotelsAfter} = this.quoteCriteria
          const flightsEnabled = flights.mode !== 'disabled'
          const promisedFlightsEnabled = this.promisedFlights.offerPromisedFlights()

          const accommodationEnabled = prePostAccommodation &&
            ((hotelsBefore && hotelsBefore.hasAvailability()) || (hotelsAfter && hotelsAfter.hasAvailability()))
          return flightsEnabled || promisedFlightsEnabled || accommodationEnabled
        }
      })()
  }

  availableTabs () {
    const tabsToExclude = []

    if (!this.extrasIsAvailable()) {
      tabsToExclude.push('extras')
    }

    return tabs.filter(tab => !tabsToExclude.includes(tab.id))
  }

  findTabById (tabId) {
    return this.availableTabs().find(tab => tab.id === tabId)
  }

  userIsAllowedToBookForThisSellingCompany () {
    const isTravelAgent = this.user && this.user.config && (this.user.isTravelAgent || this.user.whitelabel)
    if (isTravelAgent) {
      return Object.keys(this.user.config.allowedSellingCompanies).indexOf(this.config.sellingCompanyCode) !== -1
    }
    return true
  }

  userIsLoggedOutOnAgentUrl () {
    return this.user && this.user.role === 'consumer' && this.isAuthUrl && !this.user.whitelabel
  }

  continueAsConsumer () {
    delete this.isAuthUrl
  }


  routes () {
    var quoteCriteriaUrlParamBindings = this.quoteCriteriaUrlParamBindings(this.progress.criteriaLoaded)

    return [
      routes.tour({
        bindings: {
          tab: {
            get: () => this.currentTabId === 'tour-overview' ? undefined : this.currentTabId,
            set: (tab) => this.switchToTab(tab, false),
          },

          adults: quoteCriteriaUrlParamBindings.adults,
          preselectedFlight: quoteCriteriaUrlParamBindings.preselectedFlight,
          children: quoteCriteriaUrlParamBindings.children,
          childages: quoteCriteriaUrlParamBindings.childages,
          rooms: quoteCriteriaUrlParamBindings.rooms,
          epd: quoteCriteriaUrlParamBindings.epd,
          discountCode: quoteCriteriaUrlParamBindings.discountCode,
          protect: quoteCriteriaUrlParamBindings.protect,
          protectPlus: quoteCriteriaUrlParamBindings.protectPlus,
          ontario: quoteCriteriaUrlParamBindings.ontario,
          gratuities: quoteCriteriaUrlParamBindings.gratuities,
          flights: quoteCriteriaUrlParamBindings.flights,
          arrivalAirportTransfer: quoteCriteriaUrlParamBindings.arrivalAirportTransfer,
          departureAirportTransfer: quoteCriteriaUrlParamBindings.departureAirportTransfer,
          frequent: quoteCriteriaUrlParamBindings.frequent,
          hotelBefore: quoteCriteriaUrlParamBindings.hotelBefore,
          hotelAfter: quoteCriteriaUrlParamBindings.hotelAfter,
          ExternalRef: quoteCriteriaUrlParamBindings.ExternalRef,
          childrenDoBs: quoteCriteriaUrlParamBindings.childrenDoBs,
          pickupLocation: quoteCriteriaUrlParamBindings.pickupLocation,
          auth: {
            get: () => this.isAuthUrl || undefined,
            set: param => this.isAuthUrl = param == 'true',
          },
          lmdRemoved: quoteCriteriaUrlParamBindings.lmdRemoved,
        },

        push: {tab: true},

        onload: async params => {
          if (this.departure.status == 'missing') {
            return
          }

          this.anotherTab.setUpListener(() => {
            this.refresh()
          })

          if (this.isFirstArrival()) {

            urlSanitising.capitaliseParameters(params)

            if (this.isDepartureLoaded(params.sellingCompanyCode, params.tourCode, params.departureCode)) {
              const landingTab = (params.tab === 'payment' || params.tab === 'confirmation') ? () => this.getDefaultTab() : params.tab
              this.switchToTab(landingTab, false)
            } else {
              try {
                await this.load(params.sellingCompanyCode, params.tourCode, params.departureCode, params.tab)
                this.pushInitialDataToGtm()
              } catch (e) {
                console.error(e)
              }
            }
          } else {
            await this.loadCriteria(params)
          }
        },

        render: () => {
          if(this.isIE){
            return this.renderUnsupportedInfo()
          }
          if (this.departure.status === 'internalError' || this.user.status === 'internalError') {
            var currentTab = this.findTabById(this.getDefaultTab())
            this.progress.currentTab = currentTab
            this.setPageTitle(currentTab)
            this.renderPage()
          }
          if (this.departure.status == 'missing') {
            return this.render404()
          }
          if (!this.userIsAllowedToBookForThisSellingCompany()) {
            return <div class='o-user-lacks-permission'>
              {this.header}
              <nav class='main-menu' />
              <div class='o-page-content'>
                <div id='overview'>
                  <h1><b>Permission error</b></h1>
                  <p>
                     Sorry, you are unable to make a booking right now. Please contact the call centre at {this.config.phone.general} to complete this booking.
                  </p>
                </div>
              </div>
              {this.renderFooter(this.currentTab)}
            </div>
          }

          this.dmpg.sendEvents(this)
          this.setPageTitle(this.progress.currentTab)
          return this.renderPage()
        },
      }),

      router.notFound(() => this.render404()),
    ]
  }

  quoteCriteriaUrlParamBindings (criteriaLoaded) {
    if (criteriaLoaded) {
      return {
        lmdRemoved: {
          get: () => this.quoteCriteria.lmdRemoved,
        },
        epd: {
          get: () => this.quoteCriteria.discounts.epd,
        },
        ExternalRef: {
          get: () => this.quoteCriteria.ExternalRef,
        },
        discountCode: {
          get: () => {
            if (this.loadingPromotions) {
              return this.loadingPromotions
            } else if (this.quoteCriteria.promotions) {
              const manual = this.quoteCriteria.promotions.filter(p => !p.automatic && !['ADV', 'REF'].includes(p.promotionSubtype))
              if (manual.length > 0) {
                return manual.map(p => p.code).join(',')
              }
            }
          },
        },
        rooms: {
          get: () => this.quoteCriteria.selectedRoomAllocation
            ? roomsKey.stringify(this.quoteCriteria.selectedRoomAllocation.rooms)
            : undefined,
        },
        childages: {
          get: () => {
            return this.quoteCriteria.party.childAges && this.quoteCriteria.party.childAges.length ? this.quoteCriteria.party.childAges.join('-') : undefined
          },
        },
        childrenDoBs: {
          get: () => this.quoteCriteria.party.childrenDoBs && this.quoteCriteria.party.childrenDoBs.length ? this.quoteCriteria.party.childrenDoBs.join('-') : undefined,
        },
        adults: {
          get: () => this.quoteCriteria.party.adults || undefined,
        },
        preselectedFlight: {
          get: () => this.promisedFlights.buildPreselectedFlightSearchString(),
        },
        children: {
          get: () => this.quoteCriteria.party.children || undefined,
        },
        protect: {
          get: () => extrasUrlKeys.stringify(
            this.quoteCriteria.passengers,
            'travelProtection',
            TravelProtectionPlanType.standard
          ),
        },
        protectPlus: {
          get: () => extrasUrlKeys.stringify(
            this.quoteCriteria.passengers,
            'travelProtection',
            TravelProtectionPlanType.plus
          ),
        },
        ontario: {
          get: () => extrasUrlKeys.stringify(this.quoteCriteria.passengers, 'ontarioResident'),
        },
        gratuities: {
          get: () => extrasUrlKeys.stringify(this.quoteCriteria.passengers, 'prepaidGratuities'),
        },
        frequent: {
          get: () => frequentTravellersDiscountKey.stringify(this.quoteCriteria.passengers, 'frequentTravellerDiscount'),
        },
        hotelBefore: {
          get: () => extraStayKey.stringify(this.quoteCriteria.hotelsBefore.getUrlParams()),
        },
        hotelAfter: {
          get: () => extraStayKey.stringify(this.quoteCriteria.hotelsAfter.getUrlParams()),
        },
        flights: {
          get: () => this.quoteCriteria.flights.selected && this.quoteCriteria.flights.selected.id,
        },
        departureAirportTransfer: {
          get: () => this.quoteCriteria.flights.transfers.departureCode(),
        },
        arrivalAirportTransfer: {
          get: () => this.quoteCriteria.flights.transfers.arrivalCode(),
        },
        pickupLocation: {
          get: () => this.quoteCriteria.pickupLocation?.productCode,
        },
      }
    } else {
      return {
        pickupLocation: [this.tourUrlParams, 'pickupLocation'],
        lmdRemoved: [this.tourUrlParams, 'lmdRemoved'],
        epd: [this.tourUrlParams, 'epd'],
        discountCode: [this.tourUrlParams, 'discountCode'],
        rooms: [this.tourUrlParams, 'rooms'],
        adults: [this.tourUrlParams, 'adults'],
        preselectedFlight: [this.tourUrlParams, 'preselectedFlight'],
        children: [this.tourUrlParams, 'children'],
        childages: [this.tourUrlParams, 'childages'],
        protect: [this.tourUrlParams, 'protect'],
        protectPlus: [this.tourUrlParams, 'protectPlus'],
        ontario: [this.tourUrlParams, 'ontario'],
        gratuities: [this.tourUrlParams, 'gratuities'],
        flights: [this.tourUrlParams, 'flights'],
        arrivalAirportTransfer: [this.tourUrlParams, 'arrivalAirportTransfer'],
        departureAirportTransfer: [this.tourUrlParams, 'departureAirportTransfer'],
        frequent: [this.tourUrlParams, 'frequent'],
        hotelBefore: [this.tourUrlParams, 'hotelBefore'],
        hotelAfter: [this.tourUrlParams, 'hotelAfter'],
        ExternalRef: [this.tourUrlParams, 'ExternalRef'],
        childrenDoBs: [this.tourUrlParams, 'childrenDoBs'],
      }
    }
  }

  isHotelAvailable () {
    return (this.quoteCriteria.hotelsBefore.hasAvailability() || this.quoteCriteria.hotelsAfter.hasAvailability()) && this.features.extras.prePostAccommodation
  }

  setPageTitle (tab) {
    var tabTitle = () => {
      if (tab.id === 'confirmation') {
        return this.confirmationTab.pageTitle()
      } else {
        return this.getTabName(tab.id)
      }
    }

    var tourTitle = tabTitle() + (this.departure.name ? ` - ${this.departure.name}` : '')

    document.title = tourTitle
  }

  departureNotAvailable () {
    if (!availableStatus(this.departure.status)) {
      return {
        title: 'Tour departure not available',
        message: 'Please note the tour you are trying to book is no longer available.',
        renderContactMessage: this.renderErrorContactMessage(),
      }
    }
  }

  loadDepartureError () {
    if (this.departure.status == 'internalError' || this.user.status == 'internalError') {
      return {
        title: 'Problem with this tour',
        message: 'An error occurred while trying to retrieve the quote.',
        renderContactMessage: this.renderErrorContactMessage(),
      }
    }
  }

  renderErrorContactMessage () {
    return <p>Please <a href={this.config.brandPages.brandWebsite}>choose an alternate date</a> or call our reservations centre to book during regular business hours on {this.config.phone.general}.</p>
  }

  departureOutofDate () {
    const backUrl = this.departure.imminentDeparturesBackUrl

    return {
      title: 'Tour departing soon',
      message: 'Sorry, but this departure is no longer available to book online.',
      renderContactMessage: <p>To secure your place please contact us during business hours on <span class='yellow-text'>{this.config.phone.general}</span> or return to <a href={backUrl}><span class="u-no-wrap">select an alternative date</span></a></p>,
    }
  }

  loggedOutError () {
    if (this.user.loaded && this.userIsLoggedOutOnAgentUrl()) {
      return {
        title: 'You have been signed out',
        message: <div>
          <p>You will need to sign in again at <a class='travel-agent-brand-home' href={this.config.brandPages.travelAgentHomePage}>{this.config.brandPages.travelAgentHomePage}</a></p>
          <p>Logged out unexpectedly? Please try again using Microsoft Edge, ensuring you are not using private browsing mode and have third-party cookies enabled.</p>
        </div>,
      }
    }
  }

  getTabName (tabId) {
    return getTabName(this.config.brandName, tabId)
  }

  isNavigable (currentTabIndex, tabIndex) {
    const skipExtras =  this.extrasIsAvailable() && currentTabIndex === 0 && tabIndex === 2
    return !this.bookingComplete()
      && (tabIndex < currentTabIndex
      || tabIndex === currentTabIndex + 1
      || skipExtras)
      && !this.quoteCriteria.noRoomsAvailable
  }

  navigateTab ({tabId, currentTabIndex, tabIndex}) {
    if (currentTabIndex < tabIndex) {
      if (this.progress.currentTab.id !== 'tour-overview' || this.tourOverviewTab.validate()) {
        this.proceedToTab(tabId, currentTabIndex)
      }
    } else {
      this.switchToTab(tabId)
    }
  }

  loadConfirmationTab () {
    this.confirmationTab.loadTab()
  }

  loadPaymentTab () {
    this.paymentTab.loadTab()
  }

  unloadPaymentTab () {
    this.paymentTab.unloadTab()
  }

  paymentTabNavigable () {
    return this.paymentTab && this.paymentTab.navigable()
    || this.currentTabId === 'passengers'
  }

  confirmationTabNavigable () {
    return this.confirmationTab && this.confirmationTab.navigable()
  }

  findTabIndex (tabId) {
    return findIndex(tabs, tab => tab.id === tabId)
  }

  unloadTabs (fromId, toId) {
    var currentIndex = this.findTabIndex(fromId)
    var newIndex = this.findTabIndex(toId)

    for (var n = currentIndex; n > newIndex; n--) {
      var t = tabs[n]
      if (t.unloadMethod) {
        this[t.unloadMethod]()
      }
    }
  }

  switchToTab (_tabId, pushEventToGtm = true) {
    if (this.currentTabId !== _tabId) {
      this.summary && this.summary.discountCode && this.summary.discountCode.clearPromoErrors()
    }
    if (this.currentTabId == 'confirmation' || !(this.progress.tourLoaded && this.progress.criteriaLoaded)) {
      return
    }
    let tabId = _tabId || this.availableTabs()[0].id

    let tab = this.findTabById(tabId) || this.findTabById(this.getDefaultTab())

    if (tab.navigableMethod) {
      if (!this[tab.navigableMethod]()) {
        tabId = tabs[0].id
        tab = this.findTabById(tabId)
      }
    }

    if (this.currentTabId) {
      this.unloadTabs(this.currentTabId, tabId)
    }

    this.currentTabId = tabId
    this.addInlineOffers()
    window.scrollTo(0, 0)

    if (tab.loadMethod) {
      this[tab.loadMethod]()
    }

    this.progress.currentTab = tab
    this.summary.closeExpandedSummary({isMobile: false})

    if (pushEventToGtm) {
      gtmDataLayer.push({stageId: tabId, event: 'stage_changed', tourCode: this.departure.productCode})
      gtmDataLayer.activateOptimize()
    }
  }

  proceedToTab (tabId) {
    gtmDataLayer.push({stageId: tabId, event: 'stage_changed', tourCode: this.departure.productCode})
    switch (tabId) {
    case 'extras':
      return this.showExtras()
    case 'passengers':
      return this.showPassengers()
    case 'payment':
      return this.passengersTab.submit()
    }
  }

  showEmailQuote () {
    this.emailQuote = {
      contact: {
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        email: this.user.email,
      },
      friend: {},
      form: {},
    }
    this.modals.show({
      animationDuration: 0,
      render: () => this.renderEmailQuoteModal(),
      close: () => {
        delete this.emailQuote
      },
    })
  }

  fieldVisited (model, property) {
    this.validation = validation.fieldVisited(this.emailQuote, this.emailFormSchema, model, property)
  }

  validateEmailForm () {
    this.validation = validation.validate(this.emailQuote, this.emailFormSchema)
    this.showValidationErrors = !this.validation.valid
    return this.validation
  }

  async sendEmailQuote () {
    if (!this.validateEmailForm().valid) {
      return
    }
    this.showLoading()
    delete this.emailQuote.error
    try {
      await this.quoteEmail.send(this.emailQuote.contact, {friend: this.emailQuote.friend, tab: this.progress.currentTab.id})
      this.hideLoading()
      this.emailQuote.sent = true
    } catch (error) {
      this.emailQuote.error = error
    }
  }

  getTabById (tabId) {
    switch (tabId) {
    case 'tour-overview':
      return this.tourOverviewTab
    case 'extras':
      return this.extrasTab
    case 'passengers':
      return this.passengersTab
    case 'payment':
      return this.paymentTab
    case 'confirmation':
      return this.confirmationTab
    }
  }

  render404 () {
    return <div class='o-page-not-found'>
      {this.header}
      <nav class='main-menu' />
      <div class='o-page-content'>
        <div id='overview'>
          <h1><b>Oops.</b> The page you were looking for doesn't exist</h1>
          <p>You may have mistyped the address or the page may have moved.</p>
          <a href={this.config.brandPages.brandWebsite} class='btn-primary -with-arrow-left home'>Return to home</a>
        </div>
      </div>
      {this.renderFooter(this.currentTab)}
    </div>
  }

  renderUnsupportedInfo (){
    this.checkCurrentBrowser()
    return <div className ="supported-browser">
      <p>You seem to be using -  {this.currentBrowser}</p>
      <p>To proceed with your booking please continue<br/> with one of our supported browser</p>
      <p>Supported browsers:</p>
      <div className ="supported-browser__browsers">
        <a href ="https://www.google.com/chrome/" target ='_blank'><img src ="https://www.google.com/chrome/static/images/chrome-logo.svg"></img></a>
        <a href = "https://mozilla.org/en-US/firefox/new/" target ='_blank'><img src ="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a0/Firefox_logo%2C_2019.svg/1200px-Firefox_logo%2C_2019.svg.png"></img></a>
        <a href = "https://www.apple.com/safari/" target ='_blank'><img src ="https://www.apple.com/v/safari/k/images/overview/safari_icon__ep64chrczuky_large_2x.jpg"></img></a>
        <a href = "https://www.microsoft.com/en-us/edge" target ='_blank'><img src ="https://upload.wikimedia.org/wikipedia/en/thumb/9/98/Microsoft_Edge_logo_%282019%29.svg/1200px-Microsoft_Edge_logo_%282019%29.svg.png"></img></a>
      </div>
      <p>Need assistance?</p>
      <p>Call us on 1-800-367-1835</p>
    </div>
  }

  renderFooter (currentTab) {
    return this.footer ? this.footer.renderFooter(currentTab) : undefined
  }

  displayFaq (){
    return this.modals.show({
      fullscreen: true,
      render: () => <div class="booking-summary-map">
        <div className='modal__header'>
          <h3>FAQs</h3>
          <i id='faq-close-icon' className='modal__exit' onclick={(ev) => {ev.preventDefault(); return this.modals.close()}}></i>
        </div>
        {this.faq}
      </div>,
      close: () => {},
    })
  }

  renderTabs (currentTab) {
    if(this.progress.criteriaLoaded){
      return (currentTab.id !== 'confirmation')
        ? <div id='overview' class='o-dynamic-content'> { this.renderTab(currentTab) } </div>
        : this.renderTab(currentTab)
    }
  }

  renderCurrencyInfo () {
    return this.progress.tourLoaded
      ? <div class='page-footer__center'>All prices are in  <span style='padding-left: 5px;'>{this.departure.prices.currency}</span></div>
      : undefined
  }


  getItineraryImage () {
    return getItineraryImage(this.content)
  }

  renderPage () {
    const {currentTab} = this.progress
    let phoneNumbers = []
    if(this.config.phone && this.config.phone.general) {
      phoneNumbers = this.config.phone.general.split(';')
    }

    return this.quoteCriteria && this.user.loaded ?
      <div className={[{'day-tour-flow': isDayTour(this.departure)}]}>
        { this.modals }
        { this.user.isTravelAgent ? null : this.header }

        { this.anotherTab.isOpen &&
          <div className='another-tab-warning'>
            You are trying to make a booking on several different tabs which will result in the lack of confirmation email sent, or&nbsp;other&nbsp;errors. <br/> Please complete the booking in the last browser window opened.
          </div>
        }

        <nav class={['main-menu',`current_tab__${currentTab.id}`]}>
          {
            this.quoteCriteria && this.user.loaded ?
              (
                <div class='main-menu__wrapper'>
                  <div class='main-menu__brand'>
                    <a
                      href={this.user.isTravelAgent ? this.config.brandPages.travelAgentHomePage : this.config.brandPages.brandWebsite}>
                      <img class='page-nav__logo'
                        src={assetPath(`/public/img/brand-logos/${this.brandLogo}`)}
                        alt={this.config.brandName}/>
                    </a>
                    <div class='main-menu__brand-title'>Payments</div>
                  </div>
                  {responsiveRender('small', undefined, () => this.navigation.renderNavigation(currentTab, false))}
                  {
                    this.user.isTravelAgent
                      ?
                      <div class="main-menu__user">
                        <div class='page-header__content'>
                          {this.header.renderUserWelcome()}
                        </div>
                      </div>
                      :
                      this.config.sellingCompanyCode
                        ? <div class="main-menu__contact">
                          {this.config.phone.general.startsWith('http')
                            ? [<p class='main-menu__contact-title'>Can we help you?</p>,
                              <a href={phoneNumbers.shift()}>Contact us</a>,
                            ]
                            : [<p class='main-menu__contact-title'>{this.config.phone.headerTitle || 'FREEPHONE'}</p>,
                              <p class="main-menu__contact-phone">
                                <div className="page-header__phone-wrapper">
                                  <span class='page-header__phone-icon'></span>
                                  <span>{phoneNumbers.shift()}</span>
                                  { phoneNumbers.length > 0 ?
                                    <div class="page-header__dropdown-indicator">
                                      <span>▾</span>
                                    </div>
                                    : undefined}
                                  { phoneNumbers.length > 0 ?
                                    <div class="page-header__phone-dropdown">
                                      { phoneNumbers }
                                    </div>
                                    : undefined}
                                </div>
                              </p>]
                          }
                        </div>
                        : undefined
                  }
                  <button
                      id='faq-button-mobile'
                      type="button"
                      onclick={(ev) => { ev.preventDefault(); this.displayFaq(ev) }}
                      className="btn-secondary-outline faq-button-mobile"
                      data-item-type='General Interaction'
                      data-item-name='faq-cta'
                  >
                    FAQs
                  </button>
                </div>
              ) : undefined
          }
        </nav>
        <div class='o-page-content'>
          {
            this.config.features.bookWithConfidenceEnabled &&
            this.config.bookWithConfidence &&
            this.departure.prices &&
              ['tour-overview', 'extras', 'passengers', 'payment'].includes(this.progress.currentTab.id)
              ? bookWithConfidenceBanner(this.config, this.departure.prices.currency, this.quoteCriteria ? this.quoteCriteria.getDeposit() : undefined)
              : null
          }
          {this.renderDepartureError()}
          {
            (() => {
              const error = this.loggedOutError() || this.user.validateError
              if (error) {
                return <div class='o-centered-modal-wrapper'>
                  <div class='o-centered-modal server-errors'>
                    <h1 class='server-errors__heading'>{ error.title }</h1>
                    <p>{ error.message }</p>
                  </div>
                </div>
              }
            })()
          }
          {
            this.progress.criteriaLoaded
              ? <div>
                {this.genesysChat}
                <div class={['o-main-content', {'tab-confirmation': this.bookingComplete()}]}>
                  {responsiveRender('small', () => this.navigation.renderNavigation(currentTab, true))}
                  <div className='current-tab__header'>
                    <h1 class={['current-tab__title', `${currentTab.id}-header`]}>
                      {this.getTabName(currentTab.id)}
                    </h1>
                  </div>

                  {this.renderTourHeader()}
                  {this.renderTabs(currentTab)}
                </div>
                {
                  !this.bookingComplete()
                    ? <div>
                      {this.sidebar}
                    </div>
                    : ''
                }
              </div>
              : undefined
          }
        </div>

        {this.renderFooter(currentTab)}
        {this.renderLoadingBlock()}
      </div>
      : <div>
        {this.renderLoadingBlock()}
        {this.renderDepartureError()}
      </div>
  }

  renderDepartureError () {
    const error = this.departure.status === 'tooLate' ? this.departureOutofDate()
      : this.departureNotAvailable() || this.loadDepartureError()
    if (error) {
      return <div class='o-centered-modal-wrapper'>
        <div class='server-errors o-centered-modal'>
          <div className='modal__header'>
            <h3>{error.title}</h3>
          </div>
          <p>{error.message}</p>
          {error.renderContactMessage}
        </div>
      </div>
    }
  }

  renderLoadingBlockDescription (showPaymentModal) {
    if (showPaymentModal) {
      return <div class="loading-block__description">
        <p>Please wait while you are redirected to the payment page.</p>
        <p class="u-small-text u-margin-bottom-none">This can sometimes take up to 60 seconds to load, do not click back or refresh this page</p>
      </div>
    } else {
      return <div class="loading-block__description">
        {hyperdom.rawHtml('p',
          format(this.config.loadingBlockDescription,
            this.progress.criteriaLoaded ? 'updating' : 'loading'
          )
        )}
      </div>
    }
  }

  renderLoadingBlock () {
    if (this.loadingInvisible || !this.progress.tourLoading || this.loadDepartureError())
      return

    const isPaymentLoadingModal = this.progress.currentTab.id === 'passengers'
      || this.progress.currentTab.id === 'payment'
    const isOnHold = this.paymentTab && this.paymentTab.isPlacedOnHold
    const isPaymentMethodSelected = this.paymentTab && (this.paymentTab.selectedPaymentMethod === 'Credit Card' || this.paymentTab.selectedPaymentMethod === 'PayPal')
    const showPaymentModal = !isOnHold && !isPaymentMethodSelected && isPaymentLoadingModal

    return <div id="loadingBlock" class="o-centered-modal-wrapper">
      <div class="o-centered-modal loading-container">
        { (this.paymentTab && this.paymentTab.isPayButtonClicked)
          ? this.renderProcessingPaymentLoadingBlock()
          : <div>
            <div class={['loading-container__header', {'payment-tab': showPaymentModal}]}>
              <img src={assetPath(`/public/img/brand-logos/${this.brandLogo}`)} class="loading-container-brand-logo" alt={this.config.brandName} />
              <div class="loader"></div>
              { this.renderLoadingBlockDescription(showPaymentModal) }
            </div>
            {showPaymentModal ? undefined : this.renderLoadingBlockTestimonials()}
            <hr />
            <p class="u-small-text u-margin-bottom-none"><a href="#" onclick={(ev) => { ev.preventDefault(); window.fby.push(['showForm', '10148']); return false }}>Report a problem</a></p>
            <p class={['u-small-text', {'u-margin-bottom-none':showPaymentModal}]}>
              { this.config.phone.general.startsWith('http')
                ? <span>or contact us via <a href={this.config.phone.general}>{this.config.phone.general}</a></span>
                : <span>or call us on <a href={'tel:' + this.config.phone.general}>{this.config.phone.general}</a></span>
              }
            </p>
          </div>
        }
      </div>
    </div>
  }

  renderProcessingPaymentLoadingBlock () {
    return <div class='loading-container__payment'>
      <div class="loader"></div>
      We are processing your payment.
      <p class="u-small-text"> Do not refresh the page</p>
    </div>
  }

  renderLoadingBlockTestimonials () {
    if (!this.features.showRating) {
      return
    }

    return <div>
      <hr />
      <div class="testimonials">
        <div class="testimonials__stars">{FeefoRating.renderRating(5, false, true)}</div>
        <p class="testimonials__quote u-margin-bottom-none">
            "{ this.loadingTestimonial.text }"
        </p>
        {
          this.config.showFeefoLogoWithTestimonials ?
            <p>
              <span class="u-small-text u-margin-bottom-none">powered by</span>
              <img class="testimonials__feefo" src={assetPath(`/public/img/feefo.svg`)} alt="feefo - The Global Feedback Engine" />
            </p>
            : undefined
        }
      </div>
    </div>
  }

  renderValidationError (model, property) {
    if (this.showValidationErrors || (model._visited && model._visited.indexOf(property) != -1)) {
      var error = this.validation.error(model, property)
      if (error) {
        return <div class='o-form-item__warning'>
          <span>{error}</span>
        </div>
      }
    }
  }

  renderEmailQuoteModal () {
    if (!this.emailQuote) return
    return <div class='o-centered-modal-wrapper' onclick={(ev) => { ev.preventDefault(); return this.modals.close() }}>
      <div class='o-centered-modal email-quote' onclick={(ev) => ev.stopPropagation()}>
        <div className='modal__header'>
          <h3>Email me this quote</h3>
          <i className='modal__exit cancel' onclick={(ev) => { ev.preventDefault(); return this.modals.close() }}></i>
        </div>

        { this.emailQuote.sent
          ? <div class='message-success -strong u-margin-bottom-medium'>
            <p><strong>Thank you. Your quote will be emailed to you shortly.</strong></p>
          </div>
          : this.emailQuote.error
            ? <div class='message-error -strong u-margin-bottom-medium'>
              <p><strong>Sorry, something went wrong and we couldn't send your email. Please try again later.</strong></p>
            </div>
            : <form name='emailQuoteForm' id='emailQuoteForm' data-id='email-quote' class={[{submitted: this.emailQuote.form.submitted}]}
              onsubmit={(ev) => { ev.preventDefault(); return this.sendEmailQuote() }} noValidate>
              <div>
                <fieldset class='contact'>
                  <legend class='h4-like'>Contact details</legend>
                  <div class='o-form-item email-quote__email-address'>
                    <label for='mail-quote__email' class='u-visually-hidden'>e-mail address</label>
                    <input binding={[this.emailQuote.contact, 'email']}
                      id='mail-quote__email' class='o-form-item__txt-input'
                      required type={this.browserCapabilities.input.email}
                      placeholder='E-mail Address*' name='contactEmail'
                      onblur={() => this.fieldVisited(this.emailQuote.contact, 'email')} />

                    { this.renderValidationError(this.emailQuote.contact, 'email') }

                  </div>

                  <div class='o-form-item email-quote__first-name'>
                    <label for='mail-quote__first-name' class='u-visually-hidden'>First name</label>
                    <input binding={[this.emailQuote.contact, 'firstName']}
                      id='mail-quote__first-name' class='o-form-item__txt-input' required
                      name='contactFirstName' type='text' placeholder='First Name*'
                      onblur={() => this.fieldVisited(this.emailQuote.contact, 'firstName')} />

                    { this.renderValidationError(this.emailQuote.contact, 'firstName') }

                  </div>

                  <div class='o-form-item email-quote__last-name'>
                    <label for='mail-quote__last-name' class='u-visually-hidden'>Last name</label>
                    <input binding={[this.emailQuote.contact, 'lastName']} id='mail-quote__last-name'
                      class='o-form-item__txt-input' required name='contactLastName' type='text' placeholder='Last Name*'
                      onblur={() => this.fieldVisited(this.emailQuote.contact, 'lastName')} />

                    { this.renderValidationError(this.emailQuote.contact, 'lastName') }
                  </div>
                </fieldset>

                <label class='o-choice-item choice-item-send-to-friend'>
                  <input type='checkbox' class='o-choice-item__checkbox' name='send-quote-to-friend' binding={[this.emailQuote.friend, 'sendToFriend']}
                    onclick={() => this.emailQuote.friend = this.emailQuote.friend.sendToFriend ? this.emailQuote.friend : {}} />
                  <div class='o-choice-item__content'>
                    {
                      this.user.role === 'consumer' ? 'Send quote to friend' : 'Send to your client'
                    }
                  </div>
                </label>

                {
                  this.emailQuote.friend.sendToFriend
                    ? <fieldset class='u-margin-top-medium friend'>
                      <legend class='h4-like'>{this.user.role === 'consumer' ? "Friend's details" : "Client's details"}</legend>
                      <div class='o-form-item email-quote__email-address'>
                        <label for='mail-quote__friend-email' class='u-visually-hidden'>e-mail address</label>
                        <input binding={[this.emailQuote.friend, 'email']} id='mail-quote__friend-email'
                          class='o-form-item__txt-input' required name='friendEmail' type={this.browserCapabilities.input.email} placeholder='E-mail Address*'
                          onblur={() => this.fieldVisited(this.emailQuote.friend, 'email')} />
                        { this.renderValidationError(this.emailQuote.friend, 'email') }
                      </div>

                      <div class='o-form-item email-quote__first-name'>
                        <label for='mail-quote__friend-first-name' class='u-visually-hidden'>First name</label>
                        <input binding={[this.emailQuote.friend, 'firstName']} id='mail-quote__friend-first-name'
                          class='o-form-item__txt-input' required name='friendFirstName' type='text' placeholder='First Name*'
                          onblur={() => this.fieldVisited(this.emailQuote.friend, 'firstName')} />
                        { this.renderValidationError(this.emailQuote.friend, 'firstName') }
                      </div>

                      <div class='o-form-item email-quote__last-name'>
                        <label for='mail-quote__friend-last-name' class='u-visually-hidden'>Last name</label>
                        <input binding={[this.emailQuote.friend, 'lastName']} id='mail-quote__friend-last-name'
                          class='o-form-item__txt-input' required name='friendLastName' type='text' placeholder='Last Name*'
                          onblur={() => this.fieldVisited(this.emailQuote.friend, 'lastName')} />
                        { this.renderValidationError(this.emailQuote.friend, 'lastName') }
                      </div>

                    </fieldset>
                    : undefined
                }

              </div>

              <div class='u-row email-quote__cta'>
                <div>
                  <button class='btn-secondary email-quote__btn' type='submit'>Send quote</button>
                </div>
              </div>
            </form>
        }

        <div class='u-smaller-text'>
          <p>We will only use your contact details with regard to this quote, and we never share your data with third parties.</p>
          <p>By clicking the "SEND QUOTE" button you confirm that you have read and accept the terms of our <a target='_blank' href={this.config.brandPages.privacyPolicy}>Privacy Policy</a></p>
        </div>
      </div>
    </div>
  }

  bookingComplete () {
    return this.booking && (this.booking.complete() || this.booking.TourOnRequest)
  }

  renderTab (tab) {
    return this[tab.renderMethod]()
  }

  renderTourOverviewTab () {
    return this.tourOverviewTab
  }

  renderExtrasTab () {
    return this.extrasTab
  }

  renderPassengersTab () {
    return this.passengersTab
  }

  renderPaymentTab () {
    return this.paymentTab
  }

  renderConfirmationTab () {
    if (!this.booking) {
      return this.renderTourOverview()
    }

    if (!this.confirmationTab.loaded) {
      this.confirmationTab.loadTab()
    }

    return this.confirmationTab.render({
      navigatedBackAfterConfirmation: this.progress.currentTab.id !== 'confirmation',
    })
  }

  showPassengers () {
    if (this.extrasTab.validate().valid) {
      this.switchToTab('passengers')
      return false
    }
  }

  showExtras () {
    if (typeof this.summary.discountCode.promotionCodeNotAdded === 'undefined') {
      this.summary.discountCode.promotionCodeNotAdded = !!this.summary.discountCode.promotionCode

      if (!this.summary.discountCode.promotionCodeNotAdded) {
        this.switchToTab('extras')
        delete this.summary.discountCode.promotionCodeNotAdded
      }
    } else {
      this.switchToTab('extras')
    }
  }

  priceBreakdown () {
    return this.quoteCriteria.priceBreakdown()
  }

  total () {
    return this.booking.totalAmount()
  }

  totalBeforeDiscount () {
    var breakdown = this.priceBreakdown()
    return {
      amount: breakdown.totalBeforeDiscount,
      currency: breakdown.currency,
    }
  }

  discounts () {
    return discountsArray(this.priceBreakdown().discounts)
  }

  anyDiscounts () {
    return !!this.discounts().length
  }

  setLoadingInvisible (state = false) {
    this.loadingInvisible = state
  }

  nextTab () {
    if (this.tourOverviewTab.validate()) {
      this.extrasIsAvailable() ? this.showExtras() : this.showPassengers()
      this.extrasTab.extraStays.showHotelsRemovedWarning = false
    }
  }

  renderTourHeader () {
    return this.bookingComplete() || this.currentTabId === 'passengers'
      ? undefined
      : this.tourHeader.render('compact')
  }

  reloadApp () {
    location.reload()
  }
}

export default App
